// src/components/instructor/InstructorCalendarView.js
//
// Per-instructor calendar view showing their personal bookings,
// utilization heatmap, and weekly summary. Light theme.
// Used from:
//   - Admin: clicking an instructor name in the master schedule
//   - Instructor: accessible from their own dashboard

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { db } from '../../firebase';
import { getOrganizationBookings } from '../../utils/booking';
import { loadOrganizationMembers } from '../../utils/organizationData';
import { buildCalendarMonth, monthLabel } from '../../utils/calendarData';
import { getBookingChipStatus, CHIP_COLORS, classifyBookingPeriod } from '../../utils/bookingCRM';
import { resolveInstructorCategory } from '../../utils/instructorCategory';
import { isClientBooking } from '../../utils/scheduleModel';
// Pass 83B.1 §7–§10 — My Calendar day grouping + today marker follow the SELECTED ORG's IANA zone (generic
// zonedDateKey / zonedTimeLabel), never a hardcoded Asia/Tokyo path and never the browser zone.
import { zonedDateKey, zonedTimeLabel, LEGACY_OPS_TIMEZONE } from '../../utils/opsTime';

// Pass 58D — a date-only staff activity (RDO/time-off) is all-day, no startAt; it carries only serviceDate.
const isTimeoff = (b) => b.activityType === 'timeoff' || b.scheduleEntryType === 'blocked';

// ── Design tokens (light theme — matches V in OrgBookingsCRM) ──────────────

const L = {
  surface: '#FFFFFF', s2: '#F2F1EE', s3: '#ECEAE6',
  border: '#E2E0DB', borderStrong: '#C8C5BE',
  t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95',
  green: '#1D9E75', greenBg: '#E1F5EE', greenTxt: '#085041',
  amber: '#F59E0B', amberBg: '#FEF3C7', amberTxt: '#92400E',
  blue: '#2563EB', blueBg: '#EFF4FE', blueTxt: '#1A3FA0',
  purple: '#7C3AED', purpleBg: '#F5F3FF', purpleSoft: '#EDE9FE', purpleTxt: '#4C1D95',
  grayBg: '#F4F3F0', grayTxt: '#57534E',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
};

// Bar colors by period
const BAR_COLORS = { am: '#1D9E75', pm: '#2563EB', pmCollective: '#7C3AED', fd: '#F59E0B' };

// Period chip styles for detail panel
const PERIOD_CHIPS = {
  am: { bg: '#E1F5EE', c: '#085041', label: 'AM' },
  pm: { bg: '#EFF4FE', c: '#1A3FA0', label: 'PM' },
  pmCollective: { bg: '#F5F3FF', c: '#4C1D95', label: 'PM' },
  fd: { bg: '#FEF3C7', c: '#92400E', label: 'Full day' },
};

const FDI = "'Syne',sans-serif";
const FB = "'DM Sans',sans-serif";
const FM = "'DM Mono',monospace";

// ── Main component ─────────────────────────────────────────────────────────

// Pass 58D — `bookings` (optional) is the CANONICAL, own-scoped, publication-safe personal schedule
// (My Schedule passes useInstructorSchedule's output). When supplied we render it directly — NO second
// org-wide query and NO client-side identity filter, so hidden-future bookings are never downloaded and
// the instructorAuthUserId/instructorVisibleFromMs contract is preserved. When it is NOT supplied (admin
// inspecting another instructor from the org workspace), we fall back to the org query filtered by the
// canonical business instructorId. Default month is the current month in the SELECTED ORG's zone (§7/§9).
function InstructorCalendarView({ instructorId, instructorName, orgId, onBack, bookings = null, timeZone = LEGACY_OPS_TIMEZONE }) {
  const tz = timeZone || LEGACY_OPS_TIMEZONE;
  const providedMode = Array.isArray(bookings);
  // Pass 83B.3 §6/§7 — the admin/fallback own-fetch is SCOPE-BOUND to `${orgId}::${instructorId}` and stored
  // with the scope that produced it. Data is exposed ONLY when that scope matches the currently requested
  // one, so a prior org's bookings/members can never render under a new inspected scope (even on the render
  // before the effect runs). A per-run cancellation guard also ignores a late response from a prior scope.
  const requestedFetchScope = (!providedMode && orgId && instructorId) ? `${orgId}::${instructorId}` : '';
  const [fetched, setFetched] = useState({ scopeKey: '', bookings: [], members: [], loaded: false });
  const orgTodayKey = zonedDateKey(new Date(), tz); // 'YYYY-MM-DD' in the org zone
  const [curYear, setCurYear] = useState(Number(orgTodayKey.slice(0, 4)));
  const [curMonth, setCurMonth] = useState(Number(orgTodayKey.slice(5, 7)) - 1);
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  useEffect(() => {
    if (providedMode) return undefined; // personal schedule supplied → no org query
    if (!orgId || !instructorId) { setFetched({ scopeKey: '', bookings: [], members: [], loaded: true }); return undefined; }
    // stamp the NEW requested scope with empty data before the async resolves (synchronous suppression)
    setFetched({ scopeKey: requestedFetchScope, bookings: [], members: [], loaded: false });
    let cancelled = false;
    Promise.all([
      getOrganizationBookings(db, orgId).catch(() => []),
      loadOrganizationMembers(db, orgId).catch(() => []),
    ]).then(([bks, mems]) => {
      if (cancelled) return; // §7 — a late response from a prior (orgId,instructorId) scope is ignored
      setFetched({ scopeKey: requestedFetchScope, bookings: (bks || []).filter((b) => b.instructorId === instructorId), members: mems || [], loaded: true });
    });
    return () => { cancelled = true; };
  }, [providedMode, orgId, instructorId, requestedFetchScope]);

  // SYNCHRONOUS scope binding: expose fetched data only when it was produced for the current requested scope.
  const fetchMatched = !providedMode && !!requestedFetchScope && fetched.scopeKey === requestedFetchScope;
  const members = useMemo(() => (fetchMatched ? fetched.members : []), [fetchMatched, fetched]);
  const loading = providedMode ? false : !(fetchMatched && fetched.loaded);
  const allBookings = useMemo(
    () => (providedMode ? bookings : (fetchMatched ? fetched.bookings : [])),
    [providedMode, bookings, fetchMatched, fetched],
  );

  // §10 — on an org / timezone switch, immediately reset the month view + selected day so a prior org's
  // selection/day buckets never linger. (Fetched org data is already scope-suppressed above; in providedMode
  // the bookings prop is cleared upstream when useInstructorSchedule re-scopes.)
  useEffect(() => {
    const k = zonedDateKey(new Date(), tz);
    setCurYear(Number(k.slice(0, 4)));
    setCurMonth(Number(k.slice(5, 7)) - 1);
    setSelectedDateKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, tz]);

  const instructorMember = useMemo(() => members.find(m => m.userId === instructorId), [members, instructorId]);
  const discipline = instructorMember?.labels?.discipline || '';
  // Pass 46F: canonical category (drives bar colours/labels only). Pass 58D: NO pay/rate/earnings on the
  // V1 instructor calendar — this is not an authoritative payroll system. Operational schedule only.
  const category = resolveInstructorCategory(instructorMember || {});
  const isCollective = category === 'collective';

  const calData = useMemo(() => {
    // Pass 83B.2 — month aggregation (day buckets + utilisation + month stats) uses the SAME org zone as the
    // visible day cells, so a non-JST instructor's stats never fall back to Tokyo.
    const data = buildCalendarMonth({ bookings: allBookings, members, year: curYear, month: curMonth, timeZone: tz });
    return { ...data, totalSlots: 1 };
  }, [allBookings, members, curYear, curMonth, tz]);

  // Group bookings by JST date key for bar rendering. Pass 58D: a date-only RDO (no startAt) is keyed by
  // its serviceDate — so the instructor's own Day Off appears on the calendar without a fabricated startAt.
  const bookingsByDateKey = useMemo(() => {
    const map = {};
    for (const b of allBookings) {
      if (b.status === 'canceled') continue;
      let key = '';
      if (b.startAt) {
        const s = b.startAt?.toDate ? b.startAt.toDate() : b.startAt?.seconds ? new Date(b.startAt.seconds * 1000) : new Date(b.startAt);
        if (s && !Number.isNaN(s.getTime())) key = zonedDateKey(s, tz);
      } else if (b.serviceDate) {
        key = String(b.serviceDate);
      }
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    }
    return map;
  }, [allBookings, tz]);

  const selectedDayBookings = useMemo(() => {
    if (!selectedDateKey) return [];
    return bookingsByDateKey[selectedDateKey] || [];
  }, [bookingsByDateKey, selectedDateKey]);

  const shiftMonth = useCallback((delta) => {
    setCurMonth(m => {
      let nm = m + delta;
      if (nm < 0) { setCurYear(y => y - 1); return 11; }
      if (nm > 11) { setCurYear(y => y + 1); return 0; }
      return nm;
    });
  }, []);

  if (loading) return <div style={{ padding: '60px 0', textAlign: 'center', color: L.t3, fontSize: 13, fontFamily: FB }}>Loading instructor calendar...</div>;

  const { monthStats } = calData;
  const firstDow = new Date(curYear, curMonth, 1).getDay();
  const offset = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMonth = new Date(curYear, curMonth + 1, 0).getDate();
  const todayKey = zonedDateKey(new Date(), tz); // org-zone today, not browser-local
  const accentColor = isCollective ? L.purple : L.green;

  return (
    <div style={{ fontFamily: FB }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 8, padding: '6px 12px', color: L.t1, cursor: 'pointer', fontFamily: FB, fontSize: 12 }}>← Back</button>
        <div>
          <div style={{ fontFamily: FDI, fontSize: 20, fontWeight: 700, color: isCollective ? L.purple : L.t1 }}>{instructorName || 'Instructor'}</div>
          <div style={{ fontSize: 12, color: L.t2, marginTop: 2 }}>{discipline}{discipline ? ' · ' : ''}{isCollective ? 'Collective' : 'SAS Staff'}</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
        <MiniStat label="Month bookings" value={monthStats.bookedDays} color={accentColor} />
        <MiniStat label="Month util" value={monthStats.avg > 0 ? `${monthStats.avg}%` : '—'} color={accentColor} />
        <MiniStat label="Total bookings" value={allBookings.filter(b => b.status !== 'canceled').length} />
        <MiniStat label="Pool" value={isCollective ? 'Collective' : 'Internal'} color={accentColor} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14, alignItems: 'start' }}>

        {/* Calendar */}
        <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: L.shadow }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${L.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: L.t1 }}>{monthLabel(curYear, curMonth)}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <NavBtn onClick={() => shiftMonth(-1)}>◁</NavBtn>
              <NavBtn onClick={() => shiftMonth(1)}>▷</NavBtn>
            </div>
          </div>
          {/* DOW header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: L.s2, borderBottom: `1px solid ${L.border}` }}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
              <div key={i} style={{ fontSize: 10, fontWeight: 600, fontFamily: FM, color: L.t3, textAlign: 'center', padding: '5px 0', textTransform: 'uppercase' }}>{d}</div>
            ))}
          </div>
          {/* Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
            {Array.from({ length: offset }, (_, i) => (
              <div key={`e${i}`} style={{ minHeight: 64, background: L.s2, borderRight: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}` }} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const key = `${curYear}-${String(curMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayBks = bookingsByDateKey[key] || [];
              const isToday = key === todayKey;
              const isSelected = key === selectedDateKey;

              return (
                <div key={key} onClick={() => setSelectedDateKey(key)} style={{
                  minHeight: 64, padding: '4px 6px', cursor: 'pointer', position: 'relative',
                  background: isSelected ? L.s2 : L.surface,
                  outline: isSelected ? `2px solid ${accentColor}` : 'none', outlineOffset: '-2px',
                  borderRight: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}`,
                  transition: 'background 0.1s',
                }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = L.s2; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? L.s2 : L.surface; }}>
                  {/* Day number */}
                  <div style={{
                    fontSize: 11, fontWeight: 600, fontFamily: FM, color: isToday ? '#fff' : L.t2,
                    width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                    background: isToday ? L.green : 'none',
                  }}>{day}</div>
                  {/* Booking count */}
                  {dayBks.length > 0 && (
                    <div style={{ fontSize: 8, fontFamily: FM, color: L.t3, marginTop: 1 }}>{dayBks.length} bk{dayBks.length > 1 ? 's' : ''}</div>
                  )}
                  {/* Period bars at bottom — AM and PM share the same line; gray if no bookings */}
                  <DayBars bookings={dayBks} isCollective={isCollective} timeZone={tz} />
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 12, padding: '8px 14px', borderTop: `1px solid ${L.border}`, background: L.s2, alignItems: 'center', flexWrap: 'wrap' }}>
            <BarLegend color={BAR_COLORS.am} label="AM" half="left" />
            <BarLegend color={isCollective ? BAR_COLORS.pmCollective : BAR_COLORS.pm} label="PM" half="right" />
            <BarLegend color={BAR_COLORS.fd} label="Full day" />
            <BarLegend color={L.s3} label="No booking" />
          </div>
        </div>

        {/* Detail panel */}
        <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, overflow: 'hidden', position: 'sticky', top: 72, boxShadow: L.shadow }}>
          {selectedDateKey ? (
            <>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${L.border}`, background: L.s2 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: L.t1 }}>
                  {new Date(selectedDateKey + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <div style={{ fontSize: 11, color: L.t3, marginTop: 2 }}>{selectedDayBookings.length} booking{selectedDayBookings.length !== 1 ? 's' : ''}</div>
              </div>

              {selectedDayBookings.length > 0 ? selectedDayBookings.map((b, i) => {
                // Pass 58D — RDO / staff time-off: distinct all-day "Day Off", never guest/payment/lesson.
                if (isTimeoff(b) || !isClientBooking(b)) {
                  return (
                    <div key={i} data-testid="calendar-rdo" style={{ padding: '10px 16px', borderBottom: `1px solid ${L.border}`, background: L.grayBg }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 9, fontFamily: FM, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: L.s3, color: L.grayTxt }}>ALL DAY</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: L.grayTxt }}>RDO · Day Off</span>
                      </div>
                    </div>
                  );
                }
                const period = classifyBookingPeriod(b, { timeZone: tz });
                const periodChip = period === 'fd' ? PERIOD_CHIPS.fd : period === 'pm' ? (isCollective ? PERIOD_CHIPS.pmCollective : PERIOD_CHIPS.pm) : PERIOD_CHIPS.am;
                const { label: statusLabel, color: statusColor } = getBookingChipStatus(b);
                const statusChip = CHIP_COLORS[statusColor] || CHIP_COLORS.gray;

                return (
                  <div key={i} style={{ padding: '10px 16px', borderBottom: `1px solid ${L.border}`, transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = L.s2}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: L.t1 }}>{b.requesterName || 'Client'}</span>
                      <span style={{ fontSize: 9, fontFamily: FM, fontWeight: 500, padding: '1px 6px', borderRadius: 8, background: periodChip.bg, color: periodChip.c }}>{periodChip.label}</span>
                      <span style={{ fontSize: 8, fontFamily: FM, padding: '1px 5px', borderRadius: 6, background: statusChip.bg, color: statusChip.c }}>{statusLabel}</span>
                    </div>
                    <div style={{ fontSize: 11, color: L.t2 }}>{b.startAt ? zonedTimeLabel(b.startAt.toDate ? b.startAt.toDate() : (b.startAt.seconds ? new Date(b.startAt.seconds * 1000) : new Date(b.startAt)), tz) : '—'} · {b.serviceNameSnapshot || b.serviceType || 'Lesson'}</div>
                    {b.locationLabel && <div style={{ fontSize: 10, color: L.t3, marginTop: 2 }}>{b.locationLabel}</div>}
                  </div>
                );
              }) : (
                <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 11, color: L.t3 }}>No bookings this day</div>
              )}
            </>
          ) : (
            <div style={{ padding: '14px 16px', background: L.s2 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: L.t1 }}>Select a day</div>
              <div style={{ fontSize: 11, color: L.t3, marginTop: 3 }}>Click any date to see bookings</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

/**
 * Renders booking bars at the bottom of a day cell.
 * AM + PM bookings share the same horizontal line (left-half / right-half).
 * Full-day bookings get their own full-width line.
 * Max 3 lines shown; overflow shows "+N".
 */
function DayBars({ bookings, isCollective, timeZone = LEGACY_OPS_TIMEZONE }) {
  const amBks = [];
  const pmBks = [];
  const fdBks = [];
  for (const b of bookings) {
    const p = classifyBookingPeriod(b, { timeZone });
    if (p === 'fd') fdBks.push(b);
    else if (p === 'pm') pmBks.push(b);
    else amBks.push(b);
  }

  // No bookings → show a single gray "no booking" bar
  if (bookings.length === 0) {
    return (
      <div style={{ position: 'absolute', bottom: 3, left: 4, right: 4 }}>
        <div style={{ height: 3, borderRadius: 1.5, background: L.s3 }} />
      </div>
    );
  }

  // Build lines: each line is { am?: color, pm?: color } or { fd: color }
  const lines = [];

  // Full-day bookings each get their own full-width line
  for (let fi = 0; fi < fdBks.length; fi++) {
    lines.push({ fd: BAR_COLORS.fd });
  }

  // Pair AM and PM bookings onto shared lines
  const maxHalf = Math.max(amBks.length, pmBks.length);
  for (let i = 0; i < maxHalf; i++) {
    lines.push({
      am: i < amBks.length ? BAR_COLORS.am : null,
      pm: i < pmBks.length ? (isCollective ? BAR_COLORS.pmCollective : BAR_COLORS.pm) : null,
    });
  }

  const visible = lines.slice(0, 3);
  const overflow = lines.length > 3 ? lines.length - 3 : 0;

  return (
    <div style={{ position: 'absolute', bottom: 3, left: 4, right: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {visible.map((line, i) => {
        if (line.fd) {
          return <div key={i} style={{ height: 3, borderRadius: 1.5, background: line.fd }} />;
        }
        return (
          <div key={i} style={{ display: 'flex', gap: 2, height: 3 }}>
            <div style={{ flex: 1, borderRadius: 1.5, background: line.am || L.s3 }} />
            <div style={{ flex: 1, borderRadius: 1.5, background: line.pm || L.s3 }} />
          </div>
        );
      })}
      {overflow > 0 && <div style={{ fontSize: 7, color: L.t3, textAlign: 'right', lineHeight: 1 }}>+{overflow}</div>}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 8, padding: '10px 12px', boxShadow: L.shadow }}>
      <div style={{ fontSize: 9, fontFamily: FM, color: L.t3, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, fontFamily: FDI, color: color || L.t1, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function NavBtn({ onClick, children }) {
  return (
    <button onClick={onClick} style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${L.border}`, background: L.surface, color: L.t2, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FB }}>{children}</button>
  );
}

function BarLegend({ color, label, half }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: L.t3 }}>
      <div style={{ width: 24, height: 3, borderRadius: 1.5, position: 'relative', background: L.s3, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', height: '100%', borderRadius: 1.5, background: color, ...(half === 'left' ? { left: 0, width: '50%' } : half === 'right' ? { right: 0, width: '50%' } : { left: 0, width: '100%' }) }} />
      </div>
      {label}
    </div>
  );
}

export default InstructorCalendarView;
